import { supabase } from './supabase';

export interface TimeShift {
  start: string;
  end: string;
}

export interface DayAvailability {
  enabled: boolean;
  shifts: Array<{ start: string; end: string }>;
  areas?: string[];
  radius?: number;
}

export interface ResourceWorkingAvailability {
  resource_working_availability_id: string;
  resource_id: string;
  business_id: string;
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch availability for a specific resource
 */
export async function getResourceWorkingAvailability(resourceId: string): Promise<ResourceWorkingAvailability | null> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('resource_working_availability')
      .select('*')
      .eq('resource_id', resourceId)
      .maybeSingle();

    if (error) {
      // Only throw if it's not a "no rows returned" error
      if (!error.message.includes('contains 0 rows')) {
        throw error;
      }
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching resource working availability:', error);
    throw error;
  }
}

/**
 * Update availability for a specific resource
 */
export async function updateResourceWorkingAvailability(
  resourceId: string,
  availability: Partial<ResourceWorkingAvailability>
): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  try {
    const { error } = await supabase
      .from('resource_working_availability')
      .update(availability)
      .eq('resource_id', resourceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating resource working availability:', error);
    throw error;
  }
}

/**
 * Create availability for a specific resource
 */
export async function createResourceWorkingAvailability(
  resourceId: string,
  businessId: string,
  availability: Partial<ResourceWorkingAvailability>
): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  try {
    // First check if a record already exists
    const { data: existingRecord, error: fetchError } = await supabase
      .from('resource_working_availability')
      .select('*')
      .eq('resource_id', resourceId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error checking existing availability:', fetchError);
      throw fetchError;
    }

    if (existingRecord) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('resource_working_availability')
        .update({
          business_id: businessId,
          ...availability
        })
        .eq('resource_id', resourceId);

      if (updateError) throw updateError;
    } else {
      // Create new record
      const { error: insertError } = await supabase
        .from('resource_working_availability')
        .insert([
          {
            resource_id: resourceId,
            business_id: businessId,
            ...availability
          }
        ]);

      if (insertError) throw insertError;
    }
  } catch (error) {
    console.error('Error creating/updating resource working availability:', error);
    throw error;
  }
}

/**
 * Get all resources with their availability for a business
 */
export async function getBusinessResourcesWorkingAvailability(businessId: string): Promise<ResourceWorkingAvailability[]> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('resource_working_availability')
      .select('*')
      .eq('business_id', businessId);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching business resources working availability:', error);
    throw error;
  }
} 